import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { Notification, User } from "../models";
import { HelperService } from "./helper.service";
import { UserService } from "./user.service";
import { environment } from "../environments/environment";

@Injectable({
  providedIn: "root",
})
export class NotificationService {
  constructor(
    private http: HttpClient,
    private helperService: HelperService,
    private userService: UserService
  ) {}

  /**
   * Creates a collaboration message notification
   * Handles: collaboration_new_message, collaboration_file_new_message, collaboration_file_new_waveform_comment
   */
  createCollaborationMessage(
    type: "collaboration_new_message" | "collaboration_file_new_message" | "collaboration_file_new_waveform_comment",
    user: User,
    message: string,
    workspace: any,
    file?: any,
    displayTime?: string
  ): Notification {
    let title: string;
    let url: string;
    let button: string;
    let body: string;

    if (type === "collaboration_file_new_waveform_comment") {
      title = `${user.firstName} added a timestamped comment to ${file.filename}`;
      body = `Comment at ${displayTime}: ${this.helperService.htmlToText(message)}`;
      url = `https://my.presonus.com/workspaces/file/${file.id}`;
      button = "View File";
    } else if (type === "collaboration_file_new_message") {
      title = `${user.firstName} about ${file.filename}`;
      body = this.helperService.htmlToText(message);
      url = `https://my.presonus.com/workspaces/file/${file.id}`;
      button = "View File";
    } else {
      title = `${user.firstName} in ${workspace.name}`;
      body = this.helperService.htmlToText(message);
      url = `https://my.presonus.com/workspaces/detail/${workspace.id || workspace.name}`;
      button = "View Workspace";
    }

    const payload: any = {
      htmlBody: message,
      first_name: user.firstName,
      last_name: user.lastName,
      workspace_name: workspace.name,
      workspace_id: workspace.id,
      workspace_id_with_prefix: "workspace_" + workspace.id,
    };

    if (file) {
      payload.file_name = file.filename;
      payload.file_id = file.id;
    }

    return {
      title,
      body,
      photo: user.photoURL,
      topics: ["collaboration"],
      url,
      button,
      type,
      payload,
    };
  }

  /**
   * Creates a community post notification
   * Handles: community_post_reply, community_post_mention, community_pinned_post_reply, community_post_liked
   */
  createCommunityPostNotification(
    type: "community_post_reply" | "community_post_mention" | "community_pinned_post_reply" | "community_post_liked",
    user: User,
    post: any,
    reply?: any,
    originalPost?: any
  ): Notification {
    let title: string;
    let body: string;
    const url = post.id ? `https://my.presonus.com/community/post/${post.id}` : `https://my.presonus.com/community/post/${post}`;
    const button = "View Post";

    const payload: any = {
      primary_post_id: post.id || post,
    };

    switch (type) {
      case "community_post_reply":
        title = `${user.firstName} replied to your post.`;
        body = reply ? this.helperService.htmlToText(reply.message) : this.helperService.htmlToText(post.message);
        payload.htmlBody = reply ? reply.message : post.message;
        payload.original_post = {
          id: originalPost?.id || post.id || post,
          message: originalPost ? this.helperService.htmlToText(originalPost.message) : this.helperService.htmlToText(post.message),
          author: originalPost?.author || post.author,
        };
        if (reply) {
          payload.reply = {
            author: reply.author,
            message: reply.message,
          };
        }
        break;

      case "community_pinned_post_reply":
        title = `${user.firstName} commented on a post you have pinned.`;
        body = reply ? reply.message : post.message;
        payload.original_post = {
          id: originalPost?.id || post.id || post,
          message: originalPost?.message || post.message,
          author: originalPost?.author || post.author,
        };
        if (reply) {
          payload.reply = {
            author: reply.author,
            message: reply.message,
          };
        }
        break;

      case "community_post_mention":
        title = `${user.firstName} mentioned you in a post.`;
        body = this.helperService.htmlToText(post.message);
        payload.htmlBody = post.message;
        payload.post = {
          id: post.id,
          message: post.message,
          author: post.author,
        };
        if (originalPost) {
          payload.original_post = originalPost;
        }
        break;

      case "community_post_liked":
        title = `${user.firstName} ${user.lastName} liked your post in Community.`;
        body = `${user.firstName} ${user.lastName} liked "${this.helperService.htmlToText(post.message)}"`;
        payload.post = {
          id: post.id,
          message: this.helperService.htmlToText(post.message),
          author: post.author,
        };
        payload.likedBy = {
          name: `${user.firstName} ${user.lastName}`,
          id: user.id,
          photo: user.photoURL,
        };
        if (originalPost) {
          payload.original_post = {
            id: originalPost.id,
            message: this.helperService.htmlToText(originalPost.message),
            author: originalPost.author,
          };
        }
        break;
    }

    return {
      title,
      body,
      photo: user.photoURL,
      topics: ["community"],
      url,
      button,
      type,
      payload,
    };
  }

  /**
   * Creates a community direct message notification
   */
  createCommunityDirectMessage(user: User, messageObj: any): Notification {
    return {
      title: `${user.firstName} sent you a direct message.`,
      body: this.helperService.htmlToText(messageObj.message),
      photo: user.photoURL,
      topics: ["community"],
      type: "community_direct_message",
      url: `https://my.presonus.com/messages/${user.id}`,
      button: "View Message",
      payload: {
        htmlBody: messageObj.message,
        ...messageObj,
        message: this.helperService.htmlToText(messageObj.message),
      },
    };
  }

  /**
   * Creates a community connection invite notification
   */
  createCommunityConnectionInvite(user: User, connection: any, body: string): Notification {
    return {
      title: `${user.firstName} ${user.lastName} has invited you to connect in Studio One Pro+ Community!`,
      body: this.helperService.htmlToText(body),
      photo: user.photoURL,
      topics: ["community"],
      type: "community_connection_invite",
      url: `https://my.presonus.com/profile/${connection.requested_user_id}/connections?reviewConnectionFor=${user.id}`,
      button: "Review Invite",
      payload: {
        htmlBody: body,
        requested_by: user.id,
      },
    };
  }

  /**
   * Creates a new beta post notification
   */
  createNewBetaPost(user: User, post: any, betaStringId: string | boolean, betaProgramName?: string): Notification {
    const programName = betaProgramName || (typeof betaStringId === "string" ? betaStringId : "");
    return {
      title: `${user.firstName} added a post in the ${programName} beta program. - ${post.subject}`,
      body: `${this.helperService.htmlToText(post.message)}`,
      photo: user.photoURL,
      topics: ["community"],
      type: "new_beta_post",
      url: `http://my.presonus.com/beta/betapost/${post.id}?betaStringId=${betaStringId}`,
      button: "View Post",
      payload: {
        htmlBody: post.message,
        post: {
          id: post.id,
          message: this.helperService.htmlToText(post.message),
          author: post.author,
        },
        primary_post_id: post.id,
      },
    };
  }

  /**
   * Creates a collaboration workspace deleted notification
   */
  createCollaborationWorkspaceDeleted(workspace: any): Notification {
    return {
      title: `${workspace.name} was deleted.`,
      body: `The workspace ${workspace.name} was deleted. Contact the workspace owner for more information.`,
      photo: workspace.image?.url || workspace.image,
      topics: ["collaboration"],
      type: "collaboration_workspace_deleted",
      payload: {
        workspace_name: workspace.name,
        workspace_id: workspace.id,
        workspace_id_with_prefix: "workspace_" + workspace.id,
      },
    };
  }

  /**
   * Sends a notification to the backend
   * Moved from UserService
   */
  sendNotification(data: { notification: Notification; user_ids: Array<number>; clients?: Array<"rollup" | "email" | "mobile" | "email_if_no_push"> }): Observable<any> {
    return this.http.post(environment.paeApiUrl + "user/send-notification", JSON.stringify(data), this.helperService.getHttpOptions());
  }
}
